import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CaretLeft, ShoppingCart, Trash, Minus, Plus, Image as ImageIcon } from "@phosphor-icons/react";
import { Shell } from "@/components/Shell";
import { useApp } from "@/context/AppContext";
import { API } from "@/lib/api";
import { ColorSwatch, MotifThumb } from "@/components/VariantPreview";

const money = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export default function Cart() {
  const { t, cart, updateCartQty, removeFromCart } = useApp();
  const navigate = useNavigate();
  const subtotal = cart.reduce((s, x) => s + x.unit_price * x.quantity, 0);

  return (
    <Shell back="/shop">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="cart-page">
        <button data-testid="cart-back" onClick={() => navigate("/shop")} className="flex items-center gap-1 font-heading text-gray-700"><CaretLeft size={18} weight="bold" />{t("continueShopping")}</button>
        <div className="flex items-center gap-2">
          <ShoppingCart size={28} weight="duotone" style={{ color: "var(--app-strong)" }} />
          <h1 className="text-2xl font-heading font-semibold text-gray-900">{t("cartTitle")}</h1>
        </div>

        {cart.length === 0 && (
          <div className="sb-card p-6 text-center" data-testid="cart-empty">
            <p className="font-body text-gray-600">{t("emptyCart")}</p>
            <button onClick={() => navigate("/shop")} className="sb-btn mt-3 px-5 py-2.5 text-white font-medium" style={{ background: "var(--app-primary)" }}>{t("continueShopping")}</button>
          </div>
        )}

        {cart.length > 0 && (
          <>
            <div className="space-y-3" data-testid="cart-items">
              {cart.map((it) => (
                <div key={it.key} data-testid={`cart-item-${it.key}`} className="sb-card p-3 flex items-center gap-3">
                  <div className="h-14 w-14 rounded-xl border-2 border-gray-800 overflow-hidden shrink-0 flex items-center justify-center bg-gray-50">
                    {(it.motif_image_path || it.image_path) ? <img src={`${API}/files/${it.motif_image_path || it.image_path}`} alt={it.product_name} className="h-full w-full object-cover" /> : <ImageIcon size={20} className="text-gray-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-semibold text-gray-900 truncate text-sm">{it.product_name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid={`cart-previews-${it.key}`}>
                      {it.color && (
                        <span className="flex items-center gap-1">
                          <ColorSwatch color={it.color} size={18} />
                          <span className="font-body text-[11px] text-gray-600">{it.color.name}</span>
                        </span>
                      )}
                      {it.motif && (
                        <span className="flex items-center gap-1">
                          <MotifThumb motif={it.motif} size={18} />
                          <span className="font-body text-[11px] text-gray-600">{it.motif.name || "Motiv"}</span>
                        </span>
                      )}
                      <span className="font-body text-[11px] text-gray-500">× {it.quantity}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button data-testid={`cart-minus-${it.key}`} onClick={() => updateCartQty(it.key, it.quantity - 1)} className="sb-btn p-1.5 bg-white"><Minus size={14} weight="bold" /></button>
                        <span className="font-heading font-semibold w-5 text-center text-sm">{it.quantity}</span>
                        <button data-testid={`cart-plus-${it.key}`} onClick={() => updateCartQty(it.key, it.quantity + 1)} className="sb-btn p-1.5 bg-white"><Plus size={14} weight="bold" /></button>
                      </div>
                      <div className="text-right">
                        <p className="font-body text-[11px] text-gray-500">{money(it.unit_price)} / Stk.</p>
                        <p className="font-heading font-semibold">{money(it.unit_price * it.quantity)}</p>
                      </div>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${it.key}`} onClick={() => removeFromCart(it.key)} className="shrink-0 self-start text-red-500"><Trash size={18} weight="bold" /></button>
                </div>
              ))}
            </div>

            <div className="sb-card p-4 flex items-center justify-between">
              <span className="font-heading font-semibold">{t("subtotalLabel")}</span>
              <span data-testid="cart-subtotal" className="text-xl font-heading font-semibold">{money(subtotal)}</span>
            </div>

            <button data-testid="cart-checkout-btn" onClick={() => navigate("/checkout")} className="sb-btn w-full py-3.5 text-white font-medium" style={{ background: "var(--app-primary)" }}>{t("proceedCheckout")}</button>
          </>
        )}
      </motion.div>
    </Shell>
  );
}
